"use client";

import { ReactNode, useMemo } from "react";
import clsx from "clsx";

import { useActiveHeading } from "@/hooks/useActiveHeading";
import { parseContentWithToc } from "@/utils/toc";

type ArticleContentProps = {
	content: string;
	intro?: ReactNode;
	footer?: ReactNode;
	asideExtras?: ReactNode;
};

export default function ArticleContent({
	content,
	intro,
	footer,
	asideExtras,
}: ArticleContentProps) {
	const { parsedContent, tocItems } = useMemo(
		() =>
			parseContentWithToc(content, {
				headingClassName: "scroll-mt-32",
			}),
		[content]
	);

	const { activeId, setActiveId } = useActiveHeading(tocItems);
	const shouldRenderAside = tocItems.length > 0 || Boolean(asideExtras);

	return (
		<div className="mt-12 grid gap-10 lg:grid-cols-[2.5fr_1fr] items-start">
			<article className="post-content space-y-8 text-lg leading-relaxed ">
				{intro}
				{parsedContent}
				{footer}
			</article>

			{shouldRenderAside && (
				<aside className="flex flex-col gap-6 lg:sticky lg:top-20">
					{tocItems.length > 0 && (
						<div className="rounded-2xl border bg-white p-6 shadow-sm">
							<h3 className="text-lg font-semibold">Sommaire</h3>
							<nav className="mt-4 space-y-2">
								{tocItems.map((heading) => (
									<a
										key={heading.id}
										href={`#${heading.id}`}
										className={clsx(
											"block rounded-md px-2 py-1 text-sm transition-colors",
											activeId === heading.id
												? "bg-muted/60 font-semibold text-foreground"
												: "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
										)}
										onClick={() => setActiveId(heading.id)}
										aria-current={
											activeId === heading.id
												? "true"
												: undefined
										}
									>
										{heading.title}
									</a>
								))}
							</nav>
						</div>
					)}

					{asideExtras}
				</aside>
			)}
		</div>
	);
}
